import { useTranslation } from 'react-i18next';
import { AiAssist } from './AiAssist';
import { FileValueInput } from './FileValueInput';
import { ImageCollectionPicker } from './ImageCollectionPicker';
import { RecordPicker } from './RecordPicker';
import { ReferenceSourcedPreview } from './ReferenceSourcedPreview';
import { ServiceSourcedPreview } from './ServiceSourcedPreview';
import { TableGridInput } from './TableGridInput';
import { fieldLabel } from '../../data/fieldLabel';
import { customValueLengthError, parseCustomValueConfig, parseDropdownConfig } from '../../data/customValueConfig';
import { isOpenCollectionImage } from '../../data/imageCollectionField';
import { parsePickedRecord } from '../../data/referenceDefaults';
import type { LookupKind } from '../../data/lookup';
import type { FormField } from '../../data/types';

const PICKER_KINDS: Record<string, LookupKind> = {
  People: 'people',
  Company: 'companies',
  Project: 'projects',
  Contact: 'contacts'
};

/** Picker types that may also accept a manually typed name (not resolved via the client data API). */
const PICKER_CUSTOM_VALUE_TYPES = new Set(['People', 'Company', 'Project', 'Contact']);

/** Types whose picker may be restricted to one Company property in the same template. */
const LINKED_COMPANY_TYPES = new Set(['Project', 'Contact']);

interface PropertyFieldProps {
  field: FormField;
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
  /** Sibling field values, so a Project field can resolve its Linked Company property. */
  values?: Record<string, string>;
  /** Needed to preview a Reference/Library/Collection/Service-sourced Image/Table/DocumentDictionary. */
  templateId?: string;
}

function config(configJson: string): { options: string[]; source: string; collectionItem: string; columns: string[] } {
  try {
    const parsed: unknown = JSON.parse(configJson || '{}');
    const record = parsed as { options?: unknown; source?: unknown; collectionItem?: unknown; columns?: unknown };
    return {
      options: Array.isArray(record.options) ? record.options.map(String) : [],
      source: typeof record.source === 'string' ? record.source : 'upload',
      collectionItem: typeof record.collectionItem === 'string' ? record.collectionItem : '',
      columns: Array.isArray(record.columns) ? record.columns.map(String) : []
    };
  } catch {
    return { options: [], source: 'upload', collectionItem: '', columns: [] };
  }
}

/** The Project/Contact `linkedCompanyProperty` config field, if set. */
function linkedCompanyToken(configJson: string): string {
  try {
    const parsed = JSON.parse(configJson || '{}') as { linkedCompanyProperty?: unknown };
    return typeof parsed.linkedCompanyProperty === 'string' ? parsed.linkedCompanyProperty : '';
  } catch {
    return '';
  }
}

export function PropertyField({ field, value, invalid, onChange, values, templateId }: PropertyFieldProps) {
  const { t } = useTranslation();

  const { options, source, collectionItem, columns } = config(field.configJson);
  const dropdownConfig = field.propertyType === 'Dropdown' ? parseDropdownConfig(field.configJson) : null;
  const pickerConfig = PICKER_CUSTOM_VALUE_TYPES.has(field.propertyType) ? parseCustomValueConfig(field.configJson) : null;
  // A picked record travels as JSON; anything else is a value the user typed by hand.
  const isManualPickerValue = pickerConfig !== null && value.trim() !== '' && parsePickedRecord(value) === null;
  const lengthError = dropdownConfig
    ? customValueLengthError(value, dropdownConfig)
    : pickerConfig && isManualPickerValue
      ? customValueLengthError(value, pickerConfig)
      : null;

  // Only Project and Contact support a Linked Company: the sibling property's picked
  // record carries the company's id, which hard-restricts the search. A manually typed
  // company name has no id to filter by, but still counts as "chosen" — only a genuinely
  // empty company value should block the Contact/Project field.
  const companyToken = LINKED_COMPANY_TYPES.has(field.propertyType) ? linkedCompanyToken(field.configJson) : '';
  const linkedCompanyConfigured = companyToken !== '';
  const linkedCompanyValue = linkedCompanyConfigured ? (values?.[companyToken] ?? '') : '';
  const linkedCompanyId = linkedCompanyConfigured ? (parsePickedRecord(linkedCompanyValue)?.id ?? '') : '';

  const control =
    'mt-1 w-full rounded-sm border bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';
  const border = invalid ? 'border-destructive' : 'border-input';
  const describedBy = field.helpText || field.description ? `${field.tokenName}-help` : undefined;

  // A reference-backed value is chosen by the template author, not the consumer, so only
  // uploads get an input here — EXCEPT a `collection` source the author left open (no pinned
  // `collectionItem`), where the consultant picks one image per document instead.
  const isUpload = source === 'upload';
  const isImage = field.propertyType === 'Image';
  const isOpenCollection = isOpenCollectionImage(field);
  const isTable = field.propertyType === 'Table';
  const isManualEntry = source === 'manual';
  // Only Reference and Library sources exist for this type — never upload — so it's always template-provided.
  const isDocument = field.propertyType === 'DocumentDictionary';
  const isServiceSourced = source === 'service';
  const pickerKind = PICKER_KINDS[field.propertyType];

  // A textarea plus its assist block is several times the height of a single-line
  // control, so it takes a row of its own rather than stretching its neighbour's.
  const isTall = field.propertyType === 'MultilineText';

  return (
    <div className={isTall ? 'sm:col-span-2' : undefined}>
      <label className="block text-sm" htmlFor={field.tokenName}>
        <span className="font-medium text-foreground">
          {fieldLabel(field.displayName)}
          {field.isRequired ? <span className="ml-1 text-destructive">*</span> : null}
        </span>

        {(isImage || isTable) && isUpload ? (
          <FileValueInput
            id={field.tokenName}
            accept={
              isImage
                ? 'image/png,image/jpeg,image/gif'
                : '.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
            invalid={invalid}
            value={value}
            onChange={onChange}
          />
        ) : isTable && isManualEntry ? (
          <TableGridInput columns={columns} value={value} onChange={onChange} invalid={invalid} />
        ) : isOpenCollection ? (
          <ImageCollectionPicker value={value} onChange={onChange} invalid={invalid} referenceKey={field.resolvedDefault} />
        ) : (isImage || isTable || isDocument) && isServiceSourced ? (
          <ServiceSourcedPreview
            templateId={templateId}
            tokenName={field.tokenName}
            propertyType={field.propertyType}
            configJson={field.configJson}
            displayName={fieldLabel(field.displayName)}
            values={values ?? {}}
            resolvedDefault={field.resolvedDefault}
            collectionItem={collectionItem}
          />
        ) : (isImage || isTable || isDocument) && templateId && field.resolvedDefault ? (
          <ReferenceSourcedPreview
            templateId={templateId}
            propertyType={field.propertyType}
            displayName={fieldLabel(field.displayName)}
            entryKey={field.resolvedDefault}
            collectionItem={isImage && source === 'collection' ? collectionItem : undefined}
          />
        ) : isImage || isTable || isDocument ? (
          <p className="mt-1 text-sm text-muted-foreground">{t('wizard.form.notConfigured')}</p>
        ) : pickerKind ? (
          <RecordPicker
            id={field.tokenName}
            kind={pickerKind}
            invalid={invalid || lengthError !== null}
            value={value}
            onChange={onChange}
            allowCustomValue={pickerConfig?.allowCustomValue}
            linkedCompanyConfigured={linkedCompanyConfigured}
            linkedCompanyId={linkedCompanyId}
            linkedCompanyValue={linkedCompanyValue}
          />
        ) : field.propertyType === 'MultilineText' ? (
          <textarea
            id={field.tokenName}
            value={value}
            rows={4}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
            className={`${control} ${border}`}
          />
        ) : field.propertyType === 'Dropdown' && dropdownConfig?.allowCustomValue ? (
          <>
            <input
              id={field.tokenName}
              list={`${field.tokenName}-options`}
              value={value}
              aria-describedby={describedBy}
              aria-invalid={invalid || lengthError !== null}
              minLength={dropdownConfig.customMinLength}
              maxLength={dropdownConfig.customMaxLength}
              onChange={(event) => onChange(event.target.value)}
              className={`${control} ${lengthError ? 'border-destructive' : border}`}
            />
            <datalist id={`${field.tokenName}-options`}>
              {options.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </>
        ) : field.propertyType === 'Dropdown' ? (
          <select
            id={field.tokenName}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
            className={`${control} ${border}`}
          >
            <option value="">{t('wizard.form.choose')}</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : field.propertyType === 'YesNo' ? (
          <select
            id={field.tokenName}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
            className={`${control} ${border}`}
          >
            <option value="">{t('wizard.form.choose')}</option>
            <option value="Yes">{t('common.yes')}</option>
            <option value="No">{t('common.no')}</option>
          </select>
        ) : (
          <input
            id={field.tokenName}
            type={field.propertyType === 'Date' ? 'date' : field.propertyType === 'Number' ? 'number' : 'text'}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
            className={`${control} ${border}`}
          />
        )}
      </label>

      {describedBy ? (
        <p id={describedBy} className="mt-1 text-xs text-muted-foreground">
          {field.description || field.helpText}
        </p>
      ) : null}

      {field.propertyType === 'MultilineText' ? (
        <AiAssist value={value} onChange={onChange} />
      ) : null}

      {invalid ? (
        <p className="mt-1 text-xs text-destructive">{t('wizard.form.requiredField')}</p>
      ) : lengthError === 'tooShort' ? (
        <p className="mt-1 text-xs text-destructive">
          {t('wizard.form.customValueTooShort', { min: dropdownConfig?.customMinLength ?? pickerConfig?.customMinLength })}
        </p>
      ) : lengthError === 'tooLong' ? (
        <p className="mt-1 text-xs text-destructive">
          {t('wizard.form.customValueTooLong', { max: dropdownConfig?.customMaxLength ?? pickerConfig?.customMaxLength })}
        </p>
      ) : null}
    </div>
  );
}
